/**
 * Mi cuenta (pantalla F del diseño aprobado el 25-sep-2026).
 *
 * Por ahora solo trae la sección de privacidad: qué versión de los
 * documentos legales aceptó la persona y cuándo, y cómo ejercer sus
 * derechos. Cubre el derecho del titular a pedir prueba de la autorización
 * (Ley 1581, art. 8.b). El rediseño general de Mi cuenta viene después.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { LegalViewer } from "@/components/legal/LegalViewer";
import { LEGAL_DOCUMENTS, type LegalDocId } from "@/legal/documentos";
import { RESPONSABLE } from "@/legal/responsable";
import { fetchLegalAcceptances, type LegalStatusDTO } from "@/lib/api";

const FECHA = new Intl.DateTimeFormat("es-CO", {
  timeZone: "America/Bogota",
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const ROWS: { id: LegalDocId; title: string; accepted: (version: string) => string }[] = [
  {
    id: "terminos",
    title: "Términos de uso",
    accepted: (version) => `Aceptaste la versión ${version}`,
  },
  {
    id: "privacidad",
    title: "Autorización de datos personales",
    accepted: (version) => `Política de tratamiento de datos, versión ${version}`,
  },
];

export function Cuenta() {
  const [viewer, setViewer] = useState<LegalDocId | null>(null);
  const status = useQuery({ queryKey: ["legal-acceptances"], queryFn: fetchLegalAcceptances });

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <h1 className="text-2xl font-bold text-slate-900">Mi cuenta</h1>

      <section
        aria-labelledby="cuenta-privacidad-title"
        className="mt-6 rounded-xl border border-slate-200 bg-white"
        data-testid="cuenta-privacidad"
      >
        <div className="border-b border-slate-200 px-4 py-4 sm:px-5">
          <h2 id="cuenta-privacidad-title" className="text-base font-bold text-slate-900">
            Privacidad y documentos legales
          </h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Lo que aceptaste y cómo ejercer tus derechos sobre tus datos.
          </p>
        </div>

        {status.isLoading ? (
          <PrivacidadSkeleton />
        ) : status.isError ? (
          <p role="alert" className="px-4 py-4 text-sm text-red-700 sm:px-5">
            No pudimos cargar lo que aceptaste. Recarga la página; si el problema sigue, escríbenos
            a {RESPONSABLE.correoSoporte}.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {ROWS.map((row) => (
              <AcceptanceRow
                key={row.id}
                row={row}
                data={status.data}
                onOpen={() => setViewer(row.id)}
              />
            ))}
            <li className="grid grid-cols-1 gap-2 px-4 py-4 sm:grid-cols-[1fr_auto] sm:items-center sm:px-5">
              <div>
                <p className="text-sm font-bold text-slate-900">Ejercer tus derechos</p>
                <p className="text-xs text-slate-500">
                  Conocer, corregir o suprimir tus datos, o revocar la autorización. Respondemos
                  consultas en máximo 10 días hábiles y reclamos en máximo 15.
                </p>
              </div>
              <span className="text-sm font-semibold text-brand-700 select-all">
                {RESPONSABLE.correoDatos}
              </span>
            </li>
          </ul>
        )}
      </section>

      {viewer && <LegalViewer initialDoc={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}

function AcceptanceRow({
  row,
  data,
  onOpen,
}: {
  row: (typeof ROWS)[number];
  data: LegalStatusDTO | undefined;
  onOpen: () => void;
}) {
  const acceptance = data?.acceptances.find((a) => a.document === row.id);
  const pending = data?.pending.includes(row.id) ?? false;
  const doc = LEGAL_DOCUMENTS[row.id];

  return (
    <li
      data-testid={`cuenta-acceptance-${row.id}`}
      className="grid grid-cols-1 gap-2 px-4 py-4 sm:grid-cols-[1fr_auto] sm:items-center sm:px-5"
    >
      <div>
        <p className="text-sm font-bold text-slate-900">{row.title}</p>
        <p className="text-xs text-slate-500">
          {acceptance ? row.accepted(acceptance.version) : "Aún no la has aceptado"}
          {pending && acceptance && ` · la vigente es la ${doc.version}`}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:justify-end">
        {acceptance && (
          <span className="text-xs text-slate-700 tabular-nums">
            {FECHA.format(new Date(acceptance.accepted_at))}
          </span>
        )}
        <button
          type="button"
          onClick={onOpen}
          data-testid={`cuenta-open-${row.id}`}
          className="text-sm font-semibold text-brand-700 hover:underline"
        >
          Ver documento
        </button>
      </div>
    </li>
  );
}

function PrivacidadSkeleton() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="cuenta-privacidad-skeleton"
      className="motion-safe:animate-pulse"
    >
      <span className="sr-only">Cargando lo que aceptaste…</span>
      <ul aria-hidden="true" className="divide-y divide-slate-100">
        {[0, 1, 2].map((fila) => (
          <li key={fila} className="flex items-center justify-between px-4 py-4 sm:px-5">
            <div className="space-y-2">
              <div className="h-3.5 w-44 rounded bg-slate-200" />
              <div className="h-3 w-56 rounded bg-slate-100" />
            </div>
            <div className="h-3.5 w-28 rounded bg-slate-100" />
          </li>
        ))}
      </ul>
    </div>
  );
}
